import React, { useState, useCallback } from 'react';
import {
  Modal,
  Button,
  DatePicker,
  Tag,
  List,
  Progress,
  message,
  Alert,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { opcuaHistorical } from '@/services/opcService';
import type { HistoricalDataPoint } from '@/services/opcService';

const { RangePicker } = DatePicker;

// ============================================================
// 批量下载点位历史数据（真实实现）
// ============================================================

interface PointResult {
  pointId: string;
  pointName: string;
  status: 'success' | 'noData' | 'fail';
  data: { time: string; value: number }[];
}

/**
 * 导出合并数据为 CSV 文件并触发下载
 */
const exportBatchCSV = (results: PointResult[]) => {
  const header = '点位名称,点位ID,时间,值';
  const rows: string[] = [];
  for (const r of results) {
    if (r.data.length === 0) {
      rows.push(`${r.pointName},${r.pointId},--,--`);
    } else {
      for (const d of r.data) {
        rows.push(`${r.pointName},${r.pointId},${d.time},${d.value}`);
      }
    }
  }
  const csv = '\uFEFF' + [header, ...rows].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const filename = `batch_export_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`;
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  message.success(`数据已保存：${filename}`);
};

// ============================================================
// 类型定义
// ============================================================

interface PointItem {
  id: string;
  name: string;
  node_id: string;
}

interface BatchDownloadModalProps {
  open: boolean;
  onClose: () => void;
  selectedPoints: PointItem[];
  connectionId: string;
}

type Step = 'config' | 'downloading' | 'result';

// ============================================================
// BatchDownloadModal 组件
// ============================================================

const BatchDownloadModal: React.FC<BatchDownloadModalProps> = ({
  open,
  onClose,
  selectedPoints,
  connectionId,
}) => {
  // --- 步骤 ---
  const [step, setStep] = useState<Step>('config');

  // --- 时间范围 ---
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // --- 下载进度 ---
  const [downloadProgress, setDownloadProgress] = useState({
    total: 0,
    current: 0,
    success: 0,
    fail: 0,
    noData: 0,
  });
  const [downloading, setDownloading] = useState(false);

  // --- 重置 ---
  const handleReset = useCallback(() => {
    setStep('config');
    setDateRange(null);
    setDownloadProgress({ total: 0, current: 0, success: 0, fail: 0, noData: 0 });
    setDownloading(false);
  }, []);

  // --- 关闭 ---
  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  // --- 开始下载 ---
  const handleStartDownload = useCallback(async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('请选择下载时间范围');
      return;
    }

    const startTime = dateRange[0].format('YYYY-MM-DDTHH:mm:ss') + 'Z';
    const endTime = dateRange[1].format('YYYY-MM-DDTHH:mm:ss') + 'Z';

    setStep('downloading');
    setDownloading(true);
    setDownloadProgress({
      total: selectedPoints.length,
      current: 0,
      success: 0,
      fail: 0,
      noData: 0,
    });

    const results: PointResult[] = [];

    for (let i = 0; i < selectedPoints.length; i++) {
      const pt = selectedPoints[i];
      let pointResult: PointResult = {
        pointId: pt.id,
        pointName: pt.name,
        status: 'fail',
        data: [],
      };

      try {
        const res = await opcuaHistorical({
          nodeId: pt.node_id,
          startTime,
          endTime,
        });

        if (res.success && res.data && res.data.length > 0) {
          const goodData = res.data
            .filter((d: HistoricalDataPoint) => d.statusCode?.value === 0)
            .map((d: HistoricalDataPoint) => ({
              time: d.sourceTimestamp,
              value:
                typeof d.value?.value === 'number'
                  ? d.value.value
                  : parseFloat(d.value?.value) || 0,
            }));

          if (goodData.length > 0) {
            pointResult.status = 'success';
            pointResult.data = goodData;
          } else {
            pointResult.status = 'noData';
          }
        } else {
          pointResult.status = 'noData';
        }
      } catch {
        pointResult.status = 'fail';
      }

      results.push(pointResult);

      setDownloadProgress((prev) => ({
        ...prev,
        current: prev.current + 1,
        success: prev.success + (pointResult.status === 'success' ? 1 : 0),
        fail: prev.fail + (pointResult.status === 'fail' ? 1 : 0),
        noData: prev.noData + (pointResult.status === 'noData' ? 1 : 0),
      }));
    }

    // 导出 CSV
    const validResults = results.filter((r) => r.data.length > 0);
    if (validResults.length > 0) {
      exportBatchCSV(results);
    }

    setDownloading(false);
    setStep('result');
  }, [dateRange, selectedPoints]);

  // ==========================================================
  // 渲染
  // ==========================================================

  const renderConfigStep = () => (
    <div className="bdm-config">
      <div className="bdm-points-preview">
        <div className="bdm-preview-label">待下载点位（{selectedPoints.length}个）</div>
        <div className="bdm-preview-tags">
          {selectedPoints.length <= 10 ? (
            selectedPoints.map((p) => (
              <Tag key={p.id} color="blue">
                {p.name}
              </Tag>
            ))
          ) : (
            <>
              {selectedPoints.slice(0, 10).map((p) => (
                <Tag key={p.id} color="blue">
                  {p.name}
                </Tag>
              ))}
              <Tag>+{selectedPoints.length - 10} 个</Tag>
            </>
          )}
        </div>
      </div>
      <div className="bdm-date-picker">
        <div className="bdm-preview-label">选择时间范围</div>
        <RangePicker
          showTime
          style={{ width: '100%' }}
          value={dateRange as any}
          onChange={(dates) => setDateRange(dates as [Dayjs | null, Dayjs | null])}
        />
      </div>
    </div>
  );

  const renderDownloadingStep = () => {
    const { total, current, success, fail, noData } = downloadProgress;
    const percent = total > 0 ? Math.round((current / total) * 100) : 0;

    return (
      <div className="bdm-downloading">
        <div className="bdm-progress-wrapper">
          <Progress
            type="circle"
            percent={percent}
            size={100}
            status={downloading ? 'active' : 'success'}
          />
          <div className="bdm-progress-text">
            {current} / {total}
          </div>
        </div>
        <List
          size="small"
          dataSource={selectedPoints}
          renderItem={(point, index) => {
            let icon = <LoadingOutlined spin />;
            let status = 'downloading';
            if (index < current) {
              if (index < success) {
                icon = <CheckCircleOutlined style={{ color: '#00B42A' }} />;
                status = 'success';
              } else if (index < success + noData) {
                icon = <FileTextOutlined style={{ color: '#FF7D00' }} />;
                status = 'noData';
              } else {
                icon = <CloseCircleOutlined style={{ color: '#F53F3F' }} />;
                status = 'fail';
              }
            }
            return (
              <List.Item>
                <div className="bdm-point-item">
                  {icon}
                  <span className="bdm-point-name">{point.name}</span>
                  {status === 'success' && <Tag color="success">成功</Tag>}
                  {status === 'noData' && <Tag color="warning">无数据</Tag>}
                  {status === 'fail' && <Tag color="error">失败</Tag>}
                </div>
              </List.Item>
            );
          }}
          style={{ maxHeight: 250, overflow: 'auto', marginTop: 16 }}
        />
      </div>
    );
  };

  const renderResultStep = () => {
    const { success, fail, noData } = downloadProgress;
    const hasFailures = fail > 0;

    return (
      <div className="bdm-result">
        {hasFailures ? (
          <Alert
            type="warning"
            message="部分下载完成"
            description={
              <div>
                成功: {success} | 无数据: {noData} | 失败: {fail}
              </div>
            }
            showIcon
          />
        ) : (
          <Alert
            type="success"
            message={`下载完成，成功 ${success} 个点位，数据已导出为 CSV 文件`}
            showIcon
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      title="批量下载历史数据"
      open={open}
      onCancel={handleClose}
      width={560}
      footer={
        step === 'config'
          ? [
              <Button key="cancel" onClick={handleClose}>
                取消
              </Button>,
              <Button
                key="download"
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleStartDownload}
              >
                开始下载
              </Button>,
            ]
          : step === 'downloading'
            ? null
            : [
                <Button key="close" type="primary" onClick={handleClose}>
                  完成
                </Button>,
              ]
      }
      maskClosable={!downloading}
      closable={!downloading}
      destroyOnClose
    >
      {step === 'config' && renderConfigStep()}
      {step === 'downloading' && renderDownloadingStep()}
      {step === 'result' && renderResultStep()}
    </Modal>
  );
};

export default BatchDownloadModal;
